import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  User,
  Search,
  RefreshCw,
  Edit2,
  X,
  Check,
  Lock,
  Sparkles,
  Users,
  Sliders,
} from "lucide-react";
import { usersListUsers, usersUpdateUser, usersSyncAllPropelauth, usersSyncSingleUser } from "../gen";
import type { UserResponse, UserUpdateRequest } from "../gen/models";
import { useToast } from "../context/ToastContext";
import { sanitizeProtocoloName } from "../hooks/useUserDirectory";

const KNOWN_AGENT_TYPES = [
  { value: "sales", label: "Quotes" },
  { value: "frontdesk", label: "Sofia (Front Desk & Operations)" },
  { value: "el_protocolo", label: "Sofia (Legacy alias)" },
  { value: "protocolo", label: "Sofia (Legacy alias)" },
  { value: "biblioteca", label: "Analytics" },
  { value: "feedback", label: "Feedback" },
  { value: "email_drafting", label: "Email Drafter" },
];

export function UsersManagementPage() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [activeTab, setActiveTab] = useState<"all" | "agents" | "humans">("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Edit Modal State
  const [editingUser, setEditingUser] = useState<UserResponse | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editIsAgent, setEditIsAgent] = useState(false);
  const [editAgentType, setEditAgentType] = useState("");
  const [customAgentType, setCustomAgentType] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingUserId, setSyncingUserId] = useState<string | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        limit: 100,
        offset: 0,
      };
      if (activeTab === "agents") params.is_agent = true;
      if (activeTab === "humans") params.is_agent = false;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await usersListUsers(params);
      const data = (res as any).data;
      if (data && Array.isArray(data.items)) {
        setUsers(data.items);
        setTotal(data.total ?? data.items.length);
      } else {
        setUsers([]);
        setTotal(0);
      }
    } catch (err: any) {
      const msg = err?.message || "Failed to load platform users";
      setError(msg);
      showToast(msg, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [activeTab]);

  // Debounced or on-demand search
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadUsers();
  };

  const openEditModal = (user: UserResponse) => {
    setEditingUser(user);
    setEditFirstName(user.first_name || "");
    setEditLastName(user.last_name || "");
    setEditDisplayName(user.display_name || "");
    setEditIsAgent(Boolean(user.is_agent));
    const agentType = user.agent_type || "";
    const isKnown = KNOWN_AGENT_TYPES.some((a) => a.value === agentType);
    if (isKnown || !agentType) {
      setEditAgentType(agentType);
      setCustomAgentType("");
    } else {
      setEditAgentType("custom");
      setCustomAgentType(agentType);
    }
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setSaving(false);
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    setSaving(true);
    try {
      const resolvedAgentType =
        editIsAgent
          ? editAgentType === "custom"
            ? customAgentType.trim() || null
            : editAgentType || null
          : null;

      const payload: UserUpdateRequest = {
        first_name: editFirstName.trim() || null,
        last_name: editLastName.trim() || null,
        display_name: editDisplayName.trim() || null,
        is_agent: editIsAgent,
        agent_type: resolvedAgentType,
      };

      const res = await usersUpdateUser(editingUser.user_id, payload);
      const updated = (res as any).data;

      // Update local state
      setUsers((prev) =>
        prev.map((u) => (u.user_id === editingUser.user_id ? { ...u, ...updated } : u))
      );

      showToast("User identity updated successfully", { type: "success" });
      closeEditModal();
    } catch (err: any) {
      const msg = err?.message || "Failed to update user";
      showToast(msg, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSyncAllPropelauth = async () => {
    setSyncingAll(true);
    try {
      const res = await usersSyncAllPropelauth();
      const data = (res as any).data;
      if (data) {
        showToast(
          `PropelAuth sync complete: ${data.synced} users processed (${data.created} created, ${data.updated} updated)`,
          { type: "success" }
        );
      } else {
        showToast("PropelAuth sync complete", { type: "success" });
      }
      await loadUsers();
    } catch (err: any) {
      showToast(err?.message || "Failed to sync users from PropelAuth", { type: "error" });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncSingleUser = async (userId: string) => {
    setSyncingUserId(userId);
    try {
      const res = await usersSyncSingleUser(userId);
      const updated = (res as any).data;
      if (updated) {
        setUsers((prev) =>
          prev.map((u) => (u.user_id === userId ? { ...u, ...updated } : u))
        );
        showToast(`Refreshed profile from PropelAuth for ${updated.effective_name}`, {
          type: "success",
        });
      }
    } catch (err: any) {
      showToast(err?.message || `Failed to sync user ${userId} from PropelAuth`, {
        type: "error",
      });
    } finally {
      setSyncingUserId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="border-b border-stone-200 dark:border-stone-800 pb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-stone-900 dark:text-stone-100 flex items-center gap-2.5">
            <Users className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            <span>Platform Users &amp; Agents</span>
          </h1>
          <p className="text-stone-500 dark:text-stone-400 text-sm mt-1">
            Directory of team operators, human reviewers, and autonomous AI agents. Synchronized with PropelAuth authentication.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSyncAllPropelauth}
            disabled={syncingAll || loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs transition disabled:opacity-50"
            title="Fetch all users from PropelAuth into local database"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncingAll ? "animate-spin" : ""}`} />
            <span>{syncingAll ? "Syncing..." : "Sync from PropelAuth"}</span>
          </button>
          <button
            type="button"
            onClick={loadUsers}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Tabs and Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* Filter Tabs */}
        <div className="flex items-center bg-stone-100 dark:bg-stone-800/60 p-1 rounded-xl text-xs font-medium w-fit">
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`px-3 py-1.5 rounded-lg transition ${
              activeTab === "all"
                ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                : "text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
            }`}
          >
            All Users ({total})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("agents")}
            className={`px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 ${
              activeTab === "agents"
                ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                : "text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
            }`}
          >
            <Bot className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
            <span>Agents</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("humans")}
            className={`px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 ${
              activeTab === "humans"
                ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                : "text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
            }`}
          >
            <User className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span>Humans</span>
          </button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="relative max-w-xs w-full">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search name, email, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 shadow-2xs overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-stone-400 text-xs flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
            <span>Loading user directory...</span>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-rose-600 dark:text-rose-400 text-xs">{error}</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center space-y-2">
            <Users className="w-8 h-8 text-stone-300 dark:text-stone-600 mx-auto" />
            <h3 className="text-sm font-semibold text-stone-700 dark:text-stone-300">No users found</h3>
            <p className="text-xs text-stone-400 dark:text-stone-500">
              Users registered in PropelAuth will automatically sync here when webhooks fire.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-stone-100 dark:border-stone-800 bg-stone-50/75 dark:bg-stone-800/50 text-[11px] font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400">
                  <th className="py-3 px-4">User / Agent Name</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-3 text-center">Type</th>
                  <th className="py-3 px-4">Agent Identifier</th>
                  <th className="py-3 px-3 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Updated</th>
                  <th className="py-3 px-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-stone-800 font-normal text-stone-800 dark:text-stone-200">
                {users.map((u) => {
                  const isAgent = Boolean(u.is_agent);
                  return (
                    <tr
                      key={u.user_id}
                      className="hover:bg-stone-50/80 dark:hover:bg-stone-800/50 transition duration-150"
                    >
                      {/* Name & Avatar */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-semibold text-xs ${
                              isAgent
                                ? "bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                                : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700"
                            }`}
                          >
                            {isAgent ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
                              <span>{sanitizeProtocoloName(u.effective_name, "Sofia")}</span>
                              {u.display_name && (
                                <span className="text-[10px] text-stone-400 dark:text-stone-500 font-normal">
                                  ({sanitizeProtocoloName(u.display_name, "Sofia")})
                                </span>
                              )}
                            </div>
                            <span className="font-mono text-[10px] text-stone-400 dark:text-stone-500 block truncate max-w-[140px]">
                              {u.user_id}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Email (Read-Only) */}
                      <td className="py-3.5 px-4 font-mono text-stone-600 dark:text-stone-400">
                        <span className="inline-flex items-center gap-1.5">
                          <span>{u.email}</span>
                          <span title="Email is immutable (synced from PropelAuth)">
                            <Lock className="w-3 h-3 text-stone-300 dark:text-stone-600" />
                          </span>
                        </span>
                      </td>

                      {/* Type Badge */}
                      <td className="py-3.5 px-3 text-center">
                        {isAgent ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 dark:bg-purple-950/70 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 uppercase tracking-wider">
                            <Bot className="w-3 h-3" />
                            <span>Agent</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 dark:bg-blue-950/70 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                            <User className="w-3 h-3" />
                            <span>User</span>
                          </span>
                        )}
                      </td>

                      {/* Agent Type */}
                      <td className="py-3.5 px-4">
                        {u.agent_type ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono bg-stone-100 dark:bg-stone-800 text-stone-800 dark:text-stone-200 border border-stone-200 dark:border-stone-700">
                            <Sparkles className="w-3 h-3 text-amber-500" />
                            <span>{u.agent_type}</span>
                          </span>
                        ) : (
                          <span className="text-stone-400 dark:text-stone-500 text-xs">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-3 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${
                            u.status === "active"
                              ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                              : "bg-stone-100 dark:bg-stone-800 text-stone-500 border border-stone-200 dark:border-stone-700"
                          }`}
                        >
                          {u.status}
                        </span>
                      </td>

                      {/* Updated Date */}
                      <td className="py-3.5 px-4 text-right font-mono text-[11px] text-stone-500 dark:text-stone-400">
                        {u.updated_at ? new Date(u.updated_at).toLocaleDateString() : "—"}
                      </td>

                      {/* Action */}
                      <td className="py-3.5 px-3 text-center">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleSyncSingleUser(u.user_id)}
                            disabled={syncingUserId === u.user_id}
                            className="inline-flex items-center p-1 text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition disabled:opacity-50"
                            title="Fetch latest profile from PropelAuth"
                          >
                            <RefreshCw
                              className={`w-3.5 h-3.5 ${
                                syncingUserId === u.user_id ? "animate-spin text-emerald-600" : ""
                              }`}
                            />
                          </button>
                          {u.is_agent && u.agent_type && (
                            <Link
                              to={`/agents/${u.agent_type}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-stone-200 dark:border-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-700 dark:text-stone-300 transition"
                              title={`Configure ${u.agent_type} runtime and guardrails`}
                            >
                              <Sliders className="w-3 h-3 text-stone-500" />
                              <span>Config</span>
                            </Link>
                          )}
                          <button
                            type="button"
                            onClick={() => openEditModal(u)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-stone-200 dark:border-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-700 dark:text-stone-300 transition"
                          >
                            <Edit2 className="w-3 h-3 text-stone-500" />
                            <span>Edit</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/50 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-xl max-w-lg w-full p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-stone-100 dark:border-stone-800 pb-3">
              <div>
                <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                  Edit User &amp; Agent Identity
                </h3>
                <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                  Update display names and configure autonomous agent designation.
                </p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="p-1 rounded-lg text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveUser} className="space-y-4 text-xs">
              {/* User ID (Immutable) */}
              <div>
                <label className="block font-semibold text-stone-600 dark:text-stone-400 mb-1">
                  User ID (PropelAuth)
                </label>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={editingUser.user_id}
                  className="w-full px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-100 dark:bg-stone-800/60 font-mono text-stone-500 cursor-not-allowed"
                />
              </div>

              {/* Email (Immutable) */}
              <div>
                <label className="block font-semibold text-stone-600 dark:text-stone-400 mb-1 flex items-center justify-between">
                  <span>Email Address</span>
                  <span className="font-normal text-[10px] text-stone-400 flex items-center gap-1">
                    <Lock className="w-2.5 h-2.5" /> Immutable
                  </span>
                </label>
                <input
                  type="email"
                  readOnly
                  disabled
                  value={editingUser.email}
                  className="w-full px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-100 dark:bg-stone-800/60 font-mono text-stone-500 cursor-not-allowed"
                />
                <p className="text-[10px] text-stone-400 mt-1">
                  Email is authoritative in PropelAuth and cannot be changed locally.
                </p>
              </div>

              {/* First Name & Last Name */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-stone-700 dark:text-stone-300 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    placeholder="e.g. Elena"
                    className="w-full px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-stone-700 dark:text-stone-300 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    placeholder="e.g. Rojas"
                    className="w-full px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Display Name */}
              <div>
                <label className="block font-semibold text-stone-700 dark:text-stone-300 mb-1">
                  Display / Friendly Name
                </label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  placeholder="e.g. Don Silvestre or Elena (Concierge)"
                  className="w-full px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <p className="text-[10px] text-stone-400 mt-1">
                  Primary title displayed in conversation threads, scorecards, and batch reviews.
                </p>
              </div>

              {/* Agent Toggle */}
              <div className="p-3 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-800/40 space-y-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsAgent}
                    onChange={(e) => setEditIsAgent(e.target.checked)}
                    className="rounded border-stone-300 dark:border-stone-700 text-purple-600 focus:ring-purple-500 cursor-pointer w-4 h-4"
                  />
                  <div>
                    <span className="font-semibold text-stone-800 dark:text-stone-200 block">
                      Mark as Autonomous AI Agent
                    </span>
                    <span className="text-[11px] text-stone-400 block">
                      Allows this identity to be linked to agent runs and automated conversation threads.
                    </span>
                  </div>
                </label>

                {editIsAgent && (
                  <div className="pt-2 border-t border-stone-200 dark:border-stone-700/80 space-y-2">
                    <label className="block font-semibold text-stone-700 dark:text-stone-300">
                      Agent Type Identifier (`agent_type`)
                    </label>
                    <select
                      value={editAgentType}
                      onChange={(e) => setEditAgentType(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    >
                      <option value="">-- Select Agent Type --</option>
                      {KNOWN_AGENT_TYPES.map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label} ({a.value})
                        </option>
                      ))}
                      <option value="custom">Other / Custom agent_type...</option>
                    </select>

                    {editAgentType === "custom" && (
                      <input
                        type="text"
                        placeholder="e.g. concierge-bot"
                        value={customAgentType}
                        onChange={(e) => setCustomAgentType(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Form Buttons */}
              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-stone-100 dark:border-stone-800">
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition flex items-center gap-1.5 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
